/**
 * Files a hook loads by path through the resource loader, for a caller that reads any number of
 * them: a builder that resolves an ExtResource, or a CSG root whose subtree reads many `.tres`.
 * A kind of resource has its own cache and bus, so `useCachedFiles` serves them all.
 */
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useMissingResources } from '../r3f/contexts/MissingResourcesContext';
import { useCacheVersion } from './useCacheVersion';
import { usePendingWhile } from './usePendingWhile';
import { useResourceLoader } from './useResource';
import { sameEntries } from '../utils/sameEntries';
/** A mesh `.tres` loads on the generic resource bus. */
export const PARSED_RESOURCES = {
    busTypes: ['resource'],
    cacheOf: (loader) => loader.resources,
};
/** A font file loads on the font bus. */
export const FONT_FILES = { busTypes: ['font'], cacheOf: (loader) => loader.fonts };
/** A Theme file loads on the theme bus, with the fonts it names. */
export const THEME_FILES = {
    busTypes: ['theme'],
    cacheOf: (loader) => loader.themes,
};
const NO_PATHS = new Set();
const NO_BUS_TYPES = [];
/**
 * The files a plan names, as the resource loader's cache holds them: a hook cannot run once per
 * file, and a plan may name any number, in any order, with repeats. The plan reads the cache itself,
 * so a file it names only once another has loaded joins the list as that one arrives. Each file is
 * requested and pinned while the caller is mounted, and a file that fails shows as a **Missing
 * resource**. Only a load of a file the plan named re-runs the plan. A caller keeps `plan` stable
 * while its inputs hold.
 *
 * @canonical A plan of many resource files to load
 */
export function useLoadedFiles(plan) {
    return useCachedFiles(PARSED_RESOURCES, plan);
}
/**
 * {@link useLoadedFiles} for the files of `kind`. A null `kind` reads none, and its hook listens to
 * no bus: a caller that needs a kind only for some inputs passes null for the rest.
 */
export function useCachedFiles(kind, plan) {
    const ambientLoader = useResourceLoader();
    const loader = kind && ambientLoader;
    const cache = kind && loader ? kind.cacheOf(loader) : null;
    const readPaths = useRef(NO_PATHS);
    const version = useCacheVersion(loader, kind?.busTypes ?? NO_BUS_TYPES, readPaths, { onlyReadKeys: true });
    const planned = useMemo(() => (kind ? [...new Set(plan((path) => cache?.getCached(path) ?? undefined))] : []), 
    // `version` is a cache-buster: the cache changes outside React.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plan, kind, cache, version]);
    const paths = useStableList(planned);
    // A layout effect, not `useEffect`: a load that lands between the render and a later passive
    // effect would miss `readPaths`, and `onlyReadKeys` would leave the file loading for good.
    useLayoutEffect(() => {
        readPaths.current = new Set(paths);
    }, [paths]);
    useRequestedFiles(cache, paths);
    const read = useMemo(() => new Map(paths.map((path) => [path, cache?.getCached(path)])), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cache, paths, version]);
    const files = useStableEntries(read);
    useMissingFiles(files);
    const loadedFile = useMemo(() => (path) => files.get(path) ?? undefined, [files]);
    const isLoading = useMemo(() => loader !== null && hasUnsettledFile(files), [loader, files]);
    usePendingWhile(isLoading);
    return { loadedFile, isLoading };
}
function hasUnsettledFile(files) {
    for (const file of files.values())
        if (file === undefined)
            return true;
    return false;
}
/**
 * `files` under the identity of the last map with the same entries. `version` moves on every
 * resource load in the project, and a new map would rebuild the root's plan and its subtree.
 */
function useStableEntries(files) {
    const last = useRef(files);
    const stable = sameEntries(last.current, files) ? last.current : files;
    useEffect(() => {
        last.current = stable;
    }, [stable]);
    return stable;
}
/** Requests each file not yet cached, and pins each so LRU eviction spares it while mounted. */
function useRequestedFiles(cache, paths) {
    useEffect(() => {
        if (!cache)
            return undefined;
        for (const path of paths) {
            cache.pin(path);
            if (cache.getCached(path) === undefined)
                cache.request(path);
        }
        return () => paths.forEach((path) => cache.unpin(path));
    }, [cache, paths]);
}
/**
 * Reports each failed file as a **Missing resource** while it stays failed. A file this hook
 * reported that loads later was uploaded, so its row stays, marked uploaded, as `useMissingReport`
 * keeps it for one file.
 */
function useMissingFiles(files) {
    const { report, clear, markUploaded } = useMissingResources();
    const failed = useStableList([...files].filter(([, file]) => file === null).map(([path]) => path));
    const reported = useRef(NO_PATHS);
    useEffect(() => {
        failed.forEach(report);
        return () => failed.forEach(clear);
    }, [failed, report, clear]);
    useEffect(() => {
        const uploaded = [...reported.current].filter((path) => files.get(path));
        uploaded.forEach(markUploaded);
        // A path that left the list is forgotten, so its next load counts as a first load.
        reported.current = new Set([...reported.current, ...failed].filter((path) => files.has(path) && !uploaded.includes(path)));
    }, [files, failed, markUploaded]);
}
/** `list` under one identity while its entries stay the same. A path holds no newline. */
function useStableList(list) {
    const joined = list.join('\n');
    return useMemo(() => (joined ? joined.split('\n') : []), [joined]);
}
//# sourceMappingURL=useLoadedFiles.js.map